import {getUgcRenditionLink} from './ugc-media'

export const BRAND_HANDLE = {
    dynamite: 'dynamiteclothing',
    garage: 'garageclothing'
}

export const getUgcBrandHandleLabel = (brandId) =>
    BRAND_HANDLE[brandId] ? `@${BRAND_HANDLE[brandId]}` : ''

/**
 * The brand's own account can come back as the UGC creator (BV credits
 * brand-authored media with the brand). It must not render as a creator, so a
 * username/display name equal to the brand name is treated as "no creator" and
 * falls through to the brand handle.
 */
const BRAND_SELF_NAMES = {
    dynamite: 'dynamite',
    garage: 'garage'
}

const isBrandSelf = (value, brandId) =>
    Boolean(value) &&
    Boolean(BRAND_SELF_NAMES[brandId]) &&
    String(value).trim().toLowerCase() === BRAND_SELF_NAMES[brandId]

/**
 * Ticket rule: Display name -> username -> brand handle. The payload's brand
 * field is deliberately NOT used as a label; when a look has no creator we
 * credit the brand's own handle (e.g. "@dynamiteclothing") so it stays a real,
 * searchable account.
 * @param {Object} user - a BV source.user object ({username, displayName, image, ...})
 * @param {string} brandId
 */
export const getUgcCreatorLabelFromUser = (user, brandId) => {
    const displayName = isBrandSelf(user?.displayName, brandId) ? '' : user?.displayName
    const username = isBrandSelf(user?.username, brandId) ? '' : user?.username
    return displayName || username || getUgcBrandHandleLabel(brandId)
}

export const getUgcCreatorAvatarFromUser = (user) => getUgcRenditionLink(user?.image)

export const getUgcCreatorHandleFromUser = (user, brandId) =>
    user?.username && !isBrandSelf(user.username, brandId) ? `@${user.username}` : ''

/**
 * Visible creator label for the display surfaces (PDP / Home / Landing tiles):
 * prefer the real handle with a leading "@" so shoppers can recognise and
 * search/follow the creator, and only fall back to the display name then the
 * brand handle (with no prefix) when there is no username to show.
 */
export const getUgcCreatorDisplayLabel = (user, brandId) =>
    getUgcCreatorHandleFromUser(user, brandId) || getUgcCreatorLabelFromUser(user, brandId)

/**
 * Landing grid list item shape (bvUgcHelper.js normalizeMediaList):
 * `item.creatorHandle` IS the source.user object itself (not nested under
 * `source`). The payload's brand field is intentionally ignored — a look with
 * no creator credits the brand handle instead.
 */
export const getUgcTileCreatorLabel = (item, brandId) => {
    const user =
        item?.creatorHandle && typeof item.creatorHandle === 'object' ? item.creatorHandle : null
    return getUgcCreatorLabelFromUser(user, brandId)
}

/**
 * Tile variant of {@link getUgcCreatorDisplayLabel}: same handle-first rule for
 * the tile's visible creator text.
 */
export const getUgcTileCreatorDisplayLabel = (item, brandId) => {
    const user =
        item?.creatorHandle && typeof item.creatorHandle === 'object' ? item.creatorHandle : null
    return getUgcCreatorDisplayLabel(user, brandId)
}

/**
 * Resolves the creator `user` payload from either media-item shape: the expanded
 * item (`source.user`, from bvVibeService normalizeMediaItem) or a list entry
 * (`creatorHandle` IS the user object, from bvUgcHelper normalizeMediaList).
 * Accepting both lets the expanded surfaces render the creator from a list entry
 * already in memory, instead of waiting for the incoming look's request.
 */
export const getUgcItemUser = (item) => {
    if (item?.source?.user) return item.source.user
    return item?.creatorHandle && typeof item.creatorHandle === 'object' ? item.creatorHandle : null
}

/**
 * Expanded media-item shape (bvVibeService.js normalizeMediaItem): the user
 * lives at `mediaItem.source.user`, which drives the no-avatar -> @handle
 * fallback in the expanded view's creator badge. A list entry (creatorHandle) is
 * also accepted — see {@link getUgcItemUser}.
 */
export const getUgcExpandedCreatorLabel = (mediaItem, brandId) =>
    getUgcCreatorLabelFromUser(getUgcItemUser(mediaItem), brandId)

export const getUgcExpandedCreatorAvatar = (mediaItem) =>
    getUgcCreatorAvatarFromUser(getUgcItemUser(mediaItem))

export const getUgcExpandedCreatorHandle = (mediaItem, brandId) =>
    getUgcCreatorHandleFromUser(getUgcItemUser(mediaItem), brandId)
